const ACCOUNT_API_URL = 'https://account.omniapps.net/api';
const APP_NAME = 'omniapps-canvas';
const CANVAS_DATA_PREFIX = 'canvas-';

/**
 * Generate a unique canvas ID from name
 */
const generateCanvasId = (name) => {
  return `${CANVAS_DATA_PREFIX}${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
};

/**
 * Save canvas data to Omniapps Account service
 * @param {string} token - JWT token
 * @param {Object} canvasData - Canvas data object with elements, name, and optional id
 * @returns {Promise<Object>} Response data
 */
export const saveCanvasToCloud = async (token, canvasData) => {
  try {
    const canvasId = canvasData.id || generateCanvasId(canvasData.name || 'untitled');
    
    const response = await fetch(`${ACCOUNT_API_URL}/data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        appName: APP_NAME,
        dataKey: canvasId,
        dataValue: {
          id: canvasId,
          elements: canvasData.elements || canvasData,
          scale: canvasData.scale,
          stagePos: canvasData.stagePos,
          name: canvasData.name || 'Untitled Canvas',
          timestamp: new Date().toISOString(),
          version: '1.0'
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to save canvas: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving canvas to cloud:', error);
    throw error;
  }
};

/**
 * Load canvas data from Omniapps Account service
 * @param {string} token - JWT token
 * @param {string} canvasId - Optional canvas ID to load specific canvas
 * @returns {Promise<Object>} Canvas data
 */
export const loadCanvasFromCloud = async (token, canvasId = null) => {
  try {
    // If no canvasId provided, try to load the most recent canvas
    if (!canvasId) {
      const canvases = await listSavedCanvases(token);
      if (canvases.length === 0) {
        return null;
      }
      // Return the most recent canvas
      return canvases[0].dataValue;
    }

    const response = await fetch(
      `${ACCOUNT_API_URL}/data?appName=${APP_NAME}&dataKey=${canvasId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        // No data found, return null
        return null;
      }
      throw new Error(`Failed to load canvas: ${response.statusText}`);
    }

    const data = await response.json();
    return data.dataValue;
  } catch (error) {
    console.error('Error loading canvas from cloud:', error);
    throw error;
  }
};

/**
 * List all saved canvases for the user
 * @param {string} token - JWT token
 * @returns {Promise<Array>} List of saved canvases
 */
export const listSavedCanvases = async (token) => {
  try {
    const response = await fetch(
      `${ACCOUNT_API_URL}/data?appName=${APP_NAME}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      throw new Error(`Failed to list canvases: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle different response formats
    let allData = [];
    if (Array.isArray(data)) {
      allData = data;
    } else if (data.data && Array.isArray(data.data)) {
      allData = data.data;
    } else if (typeof data === 'object') {
      // Single item response
      allData = [data];
    }
    
    // The API returns all data for the app, we need to filter for canvas data
    const canvases = allData.filter(item => {
      const key = item.dataKey || item.key;
      return key && key.startsWith(CANVAS_DATA_PREFIX);
    });
    
    // Sort by timestamp, newest first
    return canvases.sort((a, b) => {
      const timeA = new Date(a.dataValue?.timestamp || 0);
      const timeB = new Date(b.dataValue?.timestamp || 0);
      return timeB - timeA;
    });
  } catch (error) {
    console.error('Error listing canvases:', error);
    return [];
  }
};

/**
 * Delete a canvas from cloud storage
 * @param {string} token - JWT token
 * @param {string} canvasId - Canvas ID to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteCanvasFromCloud = async (token, canvasId) => {
  try {
    // Use query parameters for DELETE request
    const response = await fetch(
      `${ACCOUNT_API_URL}/data?appName=${APP_NAME}&dataKey=${canvasId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete canvas: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting canvas from cloud:', error);
    throw error;
  }
};

/**
 * Check if cloud save is available (user is authenticated)
 * @param {string} token - JWT token
 * @returns {boolean}
 */
export const isCloudSaveAvailable = (token) => {
  return !!token;
};
